/**
 * Mapeo `event_name` del funnel propio → evento estándar de Meta Pixel.
 *
 * El funnel del sitio (PostHog + Slack mirror + DB) emite nombres en snake_case
 * tipo `plan_button_click`, `checkout_panel_opened`, `donation_payment_success`.
 * Meta Pixel optimiza mejor con sus eventos estándar (`Lead`, `InitiateCheckout`,
 * `Purchase`, etc.). Este módulo traduce y arma los `custom_data` que Meta espera.
 *
 * Importante:
 * - Devolvemos `null` cuando el evento no debe trackearse en Meta (ej. scroll
 *   depth, page_hidden — ruido para optimización de ads).
 * - Para Purchase, **NO** disparamos client-side en este mapper: el frontend
 *   solo confirma el éxito; la conversión real (con valor confiable) la manda
 *   el webhook server-side a través de Conversions API. Si el frontend fuera
 *   también disparase Purchase, podría doble-contar si por algún motivo el
 *   event_id no matchea. Quedan PageView + funnel intermedios para optimización.
 */

export type MetaPixelMappedEvent = {
  /** Nombre estándar Meta: PageView, ViewContent, Lead, InitiateCheckout, etc. */
  eventName: string;
  /** Params estándar (value, currency, content_name, content_ids, content_type…). */
  params: Record<string, unknown>;
};

function asNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim()) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function asString(v: unknown): string | undefined {
  if (typeof v === "string" && v.trim()) return v.trim();
  return undefined;
}

/**
 * Traduce un evento del mirror a su equivalente Meta estándar.
 * Devuelve `null` si el evento no aporta señal para optimización de ads
 * (scroll, page_hidden, time_on_page, etc.).
 */
export function mapFunnelEventToMetaPixel(
  eventName: string,
  enrichedProps: Record<string, unknown>,
): MetaPixelMappedEvent | null {
  const name = (eventName || "").toLowerCase();

  // Eventos de "ruido" — no los mandamos a Meta para no inflar volumen sin valor.
  const skipList = new Set([
    "scroll_depth",
    "time_on_page",
    "page_hidden",
    "web_vital",
    "$exception",
    "feature_flag_used",
    "posthog_initialized",
    "test_event",
  ]);
  if (skipList.has(name)) return null;

  const planTier = asString(enrichedProps.plan_tier);
  const flowName = asString(enrichedProps.flow_name);
  const productName =
    asString(enrichedProps.product_name) ||
    asString(enrichedProps.plan_name) ||
    planTier ||
    flowName ||
    eventName;
  const price = asNumber(enrichedProps.price) ?? asNumber(enrichedProps.value);
  const revenue = asNumber(enrichedProps.revenue) ?? asNumber(enrichedProps.amount);
  const currency = asString(enrichedProps.currency) ?? "USD";
  const country = asString(enrichedProps.country);
  const template = asString(enrichedProps.template) ?? asString(enrichedProps.template_type);

  const baseCommon: Record<string, unknown> = {
    content_category: template || flowName || "funnel",
    content_name: productName,
  };
  if (country) baseCommon.country = country;

  // ── Lead / form-style events ──────────────────────────────────────────────
  if (
    name === "form_start" ||
    name === "lead_captured" ||
    name === "email_captured" ||
    name === "membership_email_captured" ||
    name === "membership_email_gate_viewed" ||
    name === "dual_cta_email_captured" ||
    name === "exclusive_ar_invite_email_captured" ||
    name === "invitacion_mejores_amigos" ||
    name === "tally_form_submit" ||
    name === "email_input_interaction"
  ) {
    return { eventName: "Lead", params: baseCommon };
  }

  // ── Offer shown (50% first month) — still a soft conversion signal ─────
  if (name === "membership_email_discount_offer_shown") {
    return {
      eventName: "ViewContent",
      params: {
        ...baseCommon,
        content_type: "offer",
        content_name: "first_month_50_off",
      },
    };
  }

  // ── ViewContent: visualización de VIP video / fotos / contenido premium ──
  if (
    name === "vip_video_opened" ||
    name === "media_item_click" ||
    name === "lightbox_opened" ||
    name === "video_play" ||
    name === "photo_interaction"
  ) {
    return {
      eventName: "ViewContent",
      params: {
        ...baseCommon,
        content_type: asString(enrichedProps.media_type) || "video",
      },
    };
  }

  // ── InitiateCheckout: click en plan / checkout panel / payment modal ─────
  if (
    name === "plan_button_click" ||
    name === "checkout_panel_opened" ||
    name === "begin_checkout" ||
    name === "payment_modal_interaction" ||
    name === "donation_checkout_started" ||
    name === "membership_email_gate_continue" ||
    name === "vip_membership_checkout_initiated" ||
    name === "vip_membership_checkout_created" ||
    name === "vip_membership_payment_submitted" ||
    name === "post_stripe_return_to_clerk"
  ) {
    const params: Record<string, unknown> = {
      ...baseCommon,
      currency,
      content_type: "product",
    };
    const v = price ?? revenue;
    if (v !== undefined) params.value = v;
    if (planTier) params.content_ids = [planTier];
    return { eventName: "InitiateCheckout", params };
  }

  // ── AddPaymentInfo: usuario eligió método (stripe / mercadopago) ─────────
  if (name === "conversion_funnel_step") {
    const step = asString(enrichedProps.funnel_step);
    if (step === "payment_method_selected") {
      return {
        eventName: "AddPaymentInfo",
        params: {
          ...baseCommon,
          currency,
          ...(price !== undefined ? { value: price } : {}),
        },
      };
    }
    if (step === "modal_open") {
      return {
        eventName: "InitiateCheckout",
        params: {
          ...baseCommon,
          currency,
          ...(price !== undefined ? { value: price } : {}),
        },
      };
    }
    return null;
  }

  // CompleteRegistration / Clerk profile after Stripe
  if (name === "post_purchase_profile_completed") {
    return {
      eventName: "CompleteRegistration",
      params: {
        ...baseCommon,
        content_name: "membership_profile",
      },
    };
  }

  // ── Purchase: SOLO de eventos de confirmación client-side post-pago. ─────
  // El backend dispara su propio Purchase CAPI con valor real del webhook.
  // Acá usamos eventID compartido (= payment_intent_id / payment_id / preapproval_id)
  // para que Meta deduplique automáticamente.
  if (
    name === "donation_payment_success" ||
    name === "purchase_with_revenue" ||
    name === "purchase"
  ) {
    const params: Record<string, unknown> = {
      ...baseCommon,
      currency,
      content_type: "product",
    };
    const v = revenue ?? price;
    if (v !== undefined) params.value = v;
    return { eventName: "Purchase", params };
  }

  // ── Search ───────────────────────────────────────────────────────────────
  if (name === "search" || name === "site_search") {
    return {
      eventName: "Search",
      params: {
        ...baseCommon,
        search_string: asString(enrichedProps.search_string) || "",
      },
    };
  }

  // ── Contact: click en redes sociales que abren chat ──────────────────────
  if (name === "social_click" || name === "whatsapp_broadcast_click") {
    return {
      eventName: "Contact",
      params: {
        ...baseCommon,
        platform: asString(enrichedProps.platform) || "social",
      },
    };
  }

  // Default: no mandar a Meta (no rompemos el funnel propio, solo no le sumamos
  // ruido al pixel — mejor optimización de campañas).
  return null;
}
